import React, { useState, useEffect, useContext, createContext } from "react";
import { fetchDatabaseTranslations } from "Shared/util/web-api";
import useAppContext from "Contexts/app-context";
import { getCookie, addCookie, createCookieMaxAge } from "Shared/util/cookies";
import langSv from "Shared/languages/sv.json";
import langEn from "Shared/languages/en.json";
import langFi from "Shared/languages/fi.json";

type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : T[P] extends object
    ? RecursivePartial<T[P]>
    : T[P];
};

type LanguageRaw = typeof langSv & {
  dbTranslations: any;
};

export type Language = Omit<LanguageRaw, "brand_overrides">;

type TranslationData = {
  translations: Language;
  languageCode: string;
};

type Languages = {
  sv: Language;
  en: Language;
  fi: Language;
};

const LANGUAGE_CODE_COOKIE = "Translation.LanguageCode";

const resolveLanguageCodeByCultureCode = (cultureCode: string) => {
  switch (cultureCode) {
    case "SE":
      return "sv";
    case "FI":
      return "fi";
    case "GB":
      return "en";
    default:
      throw new Error(`Unsupported culture code '${cultureCode}'`);
  }
};

const updateCookie = (cultureCode: string): keyof Languages => {
  const selectedLanguageCode = getCookie(LANGUAGE_CODE_COOKIE);
  switch (selectedLanguageCode) {
    case "sv":
    case "en":
    case "fi":
      return selectedLanguageCode;
  }

  const defaultLanguageCode = resolveLanguageCodeByCultureCode(cultureCode);
  addCookie(LANGUAGE_CODE_COOKIE, defaultLanguageCode, createCookieMaxAge(365));
  return defaultLanguageCode;
};

type TranslationContextModel = {
  translationData: TranslationData;
  changeLanguage: (languageCode: keyof Languages) => void;
};

const TranslationContext = createContext<TranslationContextModel>(
  {} as TranslationContextModel
);

const useTranslationContext = () => {
  const context = useContext(TranslationContext);
  if (context === undefined)
    throw new Error(
      "useTranslationContext must be used within a TranslationProvider"
    );
  return context;
};

const applyBrandOverridesCore = (
  root: Language,
  overrides: RecursivePartial<Language>
) => {
  if (overrides) {
    const keys = Object.keys(overrides);
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i] as keyof typeof overrides;
      const keyOverrides = overrides[key];
      const keyRoot = root[key];
      if (keyOverrides !== undefined && keyOverrides !== null) {
        if (typeof keyOverrides === "object" && typeof keyRoot === "object") {
          root[key] = applyBrandOverridesCore(keyRoot, keyOverrides);
        } else if (
          typeof keyOverrides === "string" &&
          typeof keyRoot === "string"
        ) {
          root[key] = keyOverrides;
        }
      }
    }
  }
  return root;
};

const replaceWithPaths = (lang: any, currentPath: string) => {
  const keys = Object.keys(lang);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const myPath = `${currentPath}:${key}`;
    const obj = lang[key];
    if (typeof obj === "object" && obj !== null) {
      replaceWithPaths(obj, myPath);
    } else {
      lang[key] = `[${myPath.substr(1)}] ${obj}`;
    }
  }
};

const applyBrandOverrides = (
  langRaw: LanguageRaw,
  brandName: string
): Language => {
  const { brand_overrides, ...root } = langRaw;

  switch (brandName) {
    case "eurofinans":
    case "prioritetfinans":
    case "financelink":
      break;

    default:
      return root;
  }

  const { [brandName]: overrides } = brand_overrides;

  const query = new URLSearchParams(window.location.search);
  const displayTranslationKeys = query.has("displayTranslationKeys");
  if (displayTranslationKeys) {
    replaceWithPaths(root, "");
    return root;
  }

  return applyBrandOverridesCore(root, overrides);
};

const TranslationContextProvider = ({
  children,
}: React.PropsWithChildren<{}>) => {
  const [translations, setTranslations] = useState<Languages>({} as Languages);
  const [data, setData] = useState<TranslationData>({} as TranslationData);
  const { appData, loadingAppData } = useAppContext();

  useEffect(() => {
    if (loadingAppData) return;

    const selectedLanguageCode = updateCookie(appData.cultureCode);

    fetchDatabaseTranslations().then((result: any) => {
      const brandName = appData.brandName.toLowerCase();
      const languageArray = {
        sv: applyBrandOverrides(
          { ...langSv, dbTranslations: result.SE },
          brandName
        ),
        en: applyBrandOverrides(
          { ...langEn, dbTranslations: result.EN },
          brandName
        ),
        fi: applyBrandOverrides(
          { ...langFi, dbTranslations: result.FI },
          brandName
        ),
      };

      setTranslations(languageArray);
      setData({
        translations: languageArray[selectedLanguageCode],
        languageCode: selectedLanguageCode,
      });
    });
  }, [loadingAppData]);

  const handleChangeLanguage = (languageCode: keyof Languages) => {
    addCookie(LANGUAGE_CODE_COOKIE, languageCode, createCookieMaxAge(365));
    setData({
      translations: translations[languageCode],
      languageCode,
    });
  };

  const contextValues = {
    translationData: data,
    changeLanguage: handleChangeLanguage,
  };

  const isReady = Object.keys(data).length > 0;
  return (
    <TranslationContext.Provider value={contextValues}>
      {isReady ? children : null}
    </TranslationContext.Provider>
  );
};

export const TranslationProvider = TranslationContextProvider;

export default useTranslationContext;
